import * as React from 'react';
import { useMap } from 'react-leaflet';
import L, { TileLayer as LeafletTileLayer } from 'leaflet';
import { RasterLayer } from '../../types';

interface RasterLayerComponentProps {
  layer: RasterLayer;
  visible: boolean;
}

// Component implementation wrapped in React.memo
const RasterLayerComponentBase: React.FC<RasterLayerComponentProps> = ({ layer, visible }) => {
  const map = useMap();
  const tileLayerRef = React.useRef<LeafletTileLayer | null>(null);

  // Effect to manage the Leaflet layer instance based on visibility
  React.useEffect(() => {
    if (!map) return;

    // Create the layer instance if it doesn't exist
    if (!tileLayerRef.current) {
      tileLayerRef.current = L.tileLayer(layer.url, {
        minZoom: layer.minZoom,
        maxZoom: layer.maxZoom,
        opacity: layer.opacity,
        tileSize: 512,
        zoomOffset: -1,
        errorTileUrl: '',
        bounds: layer.bounds, // Leaflet will handle bounds checking
        minNativeZoom: layer.minNativeZoom,
        maxNativeZoom: layer.maxNativeZoom,
      });
      console.log(`Layer ${layer.id} instance created with tileSize 512 and CORS handling.`);
    }

    // Add or remove based on visibility prop
    if (visible) {
      if (!map.hasLayer(tileLayerRef.current)) {
        tileLayerRef.current.addTo(map);
        console.log(`Layer ${layer.id} added to map.`);
      }
    } else {
      if (map.hasLayer(tileLayerRef.current)) {
        map.removeLayer(tileLayerRef.current);
        console.log(`Layer ${layer.id} removed from map.`);
      }
    }

    // Cleanup: Remove layer if it's on the map when the component unmounts
    return () => {
      if (tileLayerRef.current && map.hasLayer(tileLayerRef.current)) {
        map.removeLayer(tileLayerRef.current);
        console.log(`Layer ${layer.id} cleaned up.`);
      }
    };
  }, [map, visible, layer.id, layer.url, layer.minZoom, layer.maxZoom, layer.minNativeZoom, layer.maxNativeZoom, layer.opacity, layer.bounds]);

  return null; // Component doesn't render directly
};

// Custom comparison function that does deep comparison of the layer object
const arePropsEqual = (prevProps: RasterLayerComponentProps, nextProps: RasterLayerComponentProps) => {
  return prevProps.visible === nextProps.visible &&
    prevProps.layer.id === nextProps.layer.id &&
    prevProps.layer.url === nextProps.layer.url &&
    prevProps.layer.minZoom === nextProps.layer.minZoom &&
    prevProps.layer.maxZoom === nextProps.layer.maxZoom &&
    prevProps.layer.opacity === nextProps.layer.opacity &&
    JSON.stringify(prevProps.layer.bounds) === JSON.stringify(nextProps.layer.bounds);
};

// Export memoized component
export const RasterLayerComponent = React.memo(RasterLayerComponentBase, arePropsEqual);
